import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIKED_KEY = 'mindesign_liked_songs_v1';
const SKIPPED_KEY = 'mindesign_skipped_songs_v1';

const shuffle = (arr) => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const readSet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (err) {
    if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.debug(`[radio] readSet(${key}) failed:`, err);
    }
    return new Set();
  }
};

const writeSet = (key, set) => {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch (err) {
    // quota exceeded or private-browsing mode — preferences just won't persist.
    if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.debug(`[radio] writeSet(${key}) failed:`, err);
    }
  }
};

export const RadioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);          // shuffled, filtered
  const [allSongs, setAllSongs] = useState([]);    // raw list, unfiltered
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [history, setHistory] = useState([]);
  // Per-browser preference state, persisted to localStorage
  const [liked, setLiked] = useState(() => (typeof window !== 'undefined' ? readSet(LIKED_KEY) : new Set()));
  const [skipped, setSkipped] = useState(() => (typeof window !== 'undefined' ? readSet(SKIPPED_KEY) : new Set()));

  // Lazy-create the audio element on the client
  if (typeof window !== 'undefined' && !audioRef.current) {
    const a = new Audio();
    a.preload = 'metadata';
    audioRef.current = a;
  }

  // Flag set when the previous track ended naturally — tells the audioSrc
  // effect to force-play the next track even if a stray 'pause' event has
  // briefly flipped `playing` to false (browser fires 'pause' on track-end).
  const wantAutoPlayRef = useRef(false);

  // Initial load — pull EVERY page so the queue mirrors the full library
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        let page = 1;
        while (true) {
          const res = await fetch(`${API_URL}/api/music/songs?limit=100&page=${page}`);
          const data = await res.json();
          all.push(...(data?.songs || []));
          if (page >= (data?.total_pages || 1)) break;
          page += 1;
          if (page > 30) break; // hard safety cap
        }
        const songs = all.filter((s) => !!s.full_audio_url);
        if (!cancelled) {
          setAllSongs(songs);
          // Filter out skipped tracks from the active queue
          const initialSkipped = readSet(SKIPPED_KEY);
          const playable = songs.filter((s) => !initialSkipped.has(s.id));
          setQueue(shuffle(playable.length ? playable : songs));
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Broadcast temporarily offline.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const current = queue[index];
  const audioSrc = current
    ? current.full_audio_url.startsWith('http')
      ? current.full_audio_url
      : `${API_URL}${current.full_audio_url}`
    : '';

  // Update audio src when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return;
    audio.src = audioSrc;
    audio.load();
    // Auto-play if we're already broadcasting OR this is a programmatic
    // advance from a track ending naturally. Without this, the 'pause' event
    // fired by the browser at end-of-track silently kills continuous play.
    if (playing || wantAutoPlayRef.current) {
      wantAutoPlayRef.current = false;
      audio.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
    if (hasStarted && current?.id) {
      fetch(`${API_URL}/api/music/songs/${current.id}`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc]);

  const next = useCallback(() => {
    setIndex((i) => {
      if (queue.length === 0) return i;
      if (queue[i]) {
        setHistory((h) => [queue[i], ...h].slice(0, 5));
      }
      return (i + 1) % queue.length;
    });
  }, [queue]);

  const prev = useCallback(() => {
    setIndex((i) => {
      if (queue.length === 0) return i;
      return (i - 1 + queue.length) % queue.length;
    });
  }, [queue]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    try {
      if (audio.paused) {
        await audio.play();
        setPlaying(true);
        setHasStarted(true);
      } else {
        audio.pause();
        setPlaying(false);
      }
    } catch (e) {
      setError('Browser blocked playback. Press play again.');
    }
  }, [current]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setMuted(audio.muted);
  }, []);

  // ❤️ Like / Unlike the currently playing track
  const toggleLikeCurrent = useCallback(() => {
    if (!current?.id) return;
    setLiked((prev) => {
      const nextSet = new Set(prev);
      const wasLiked = nextSet.has(current.id);
      if (wasLiked) {
        nextSet.delete(current.id);
        fetch(`${API_URL}/api/music/songs/${current.id}/unlike`, { method: 'POST' }).catch(() => {});
      } else {
        nextSet.add(current.id);
        fetch(`${API_URL}/api/music/songs/${current.id}/like`, { method: 'POST' }).catch(() => {});
      }
      writeSet(LIKED_KEY, nextSet);
      return nextSet;
    });
  }, [current]);

  // ⏭ Skip & remember — drop the track from the active queue + persist
  const skipAndRemember = useCallback(() => {
    if (!current?.id) return;
    const skippedId = current.id;
    setSkipped((prev) => {
      const nextSet = new Set(prev);
      nextSet.add(skippedId);
      writeSet(SKIPPED_KEY, nextSet);
      return nextSet;
    });
    fetch(`${API_URL}/api/music/songs/${skippedId}/skip-vote`, { method: 'POST' }).catch(() => {});
    // Remove from queue and advance
    setQueue((q) => {
      const filtered = q.filter((s) => s.id !== skippedId);
      // Adjust index so we land on the same logical position
      setIndex((i) => Math.min(i, Math.max(0, filtered.length - 1)));
      return filtered.length ? filtered : q;
    });
    next();
  }, [current, next]);

  const restoreSkipped = useCallback(() => {
    setSkipped(() => {
      const empty = new Set();
      writeSet(SKIPPED_KEY, empty);
      return empty;
    });
    if (allSongs.length) {
      setQueue(shuffle(allSongs));
      setIndex(0);
    }
  }, [allSongs]);

  // Wire audio element listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => {
      // Mark this advance as automatic so:
      //   1. the audioSrc effect force-plays the next track (continuous rotation)
      //   2. the NowPlayingToast component knows to pop a notification
      wantAutoPlayRef.current = true;
      try { window.dispatchEvent(new CustomEvent('mindesign:auto-advance')); } catch (_) {}
      next();
    };
    const onPlay = () => setPlaying(true);
    // Only treat a 'pause' as a real pause if the audio isn't ended/seeking.
    // The browser fires 'pause' on natural end-of-track which used to flip
    // `playing` off and break continuous play. Now we ignore it.
    const onPause = () => {
      if (!audio.ended) setPlaying(false);
    };
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [next]);

  const isCurrentLiked = current ? liked.has(current.id) : false;

  const value = useMemo(
    () => ({
      queue,
      allSongs,
      index,
      current,
      playing,
      muted,
      loading,
      error,
      hasStarted,
      history,
      liked,
      skipped,
      isCurrentLiked,
      togglePlay,
      next,
      prev,
      toggleMute,
      toggleLikeCurrent,
      skipAndRemember,
      restoreSkipped,
    }),
    [
      queue, allSongs, index, current, playing, muted, loading, error, hasStarted, history,
      liked, skipped, isCurrentLiked,
      togglePlay, next, prev, toggleMute, toggleLikeCurrent, skipAndRemember, restoreSkipped,
    ]
  );

  return <RadioContextInternal.Provider value={value}>{children}</RadioContextInternal.Provider>;
};

const RadioContextInternal = createContext(null);

export const useRadio = () => {
  const ctx = useContext(RadioContextInternal);
  if (!ctx) {
    throw new Error('useRadio must be used inside <RadioProvider>');
  }
  return ctx;
};
